import { Button, StyledText } from 'UI';
import { ManualTypeEnum } from 'api/generated';
import httpClient from 'api/https-client';
import { Loader, Sidebar, UpgradeSubscriptionModal } from 'components';
import { routes } from 'constant/routes';
import { useNotifications, useToggle } from 'hooks';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import {
  READER_ORIGIN,
  ReaderInboundMessage,
  ReaderThemeMode,
  ReaderTokenResponse,
  canUserAccessTextbook,
  isReaderAvailable,
  parseReaderMessage,
  postToReader,
  registerReaderFrame,
} from 'utils';

const Reader: FC = () => {
  const { errorToast } = useNotifications();
  const dispatch = useAppDispatch();
  const { isOpen: isUpgradeModalOpen, open: openUpgradeModal, close: closeUpgradeModal } = useToggle();

  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);
  const courseId = activeCourse?.id;

  // The reader is cross-origin, so it cannot read our theme class, our CSS or our localStorage —
  // we have to tell it. This is the Profile page's stored Dark mode choice, not the OS setting,
  // so it is only ever 'dark' or 'light'; the reader's third value, 'system', is never sent.
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);
  const themeMode: ReaderThemeMode = isDarkMode ? 'dark' : 'light';

  // Preload logos of all enrolled courses so switching textbook/course swaps logos instantly
  useEffect(() => {
    if (!courses || !Array.isArray(courses)) return;
    courses.forEach((c) => {
      const lightLogo = c.courseLogoUrl?.trim();
      const darkLogo = c.courseDarkLogoUrl?.trim();
      if (lightLogo) {
        const img = new Image();
        img.src = lightLogo;
      }
      if (darkLogo) {
        const img = new Image();
        img.src = darkLogo;
      }
    });
  }, [courses]);

  const [frameSrc, setFrameSrc] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  /**
   * Driven entirely by the reader's `ipta-reader:chrome` messages (scroll-away and AI-tutor
   * full-screen). The reader emits on phone viewports only, so desktop never collapses.
   */
  const [isChromeCollapsed, setIsChromeCollapsed] = useState(false);

  const frameRef = useRef<HTMLIFrameElement>(null);
  // The course the mounted frame is currently signed in for; undefined until the frame exists.
  const openedCourseIdRef = useRef<string | undefined>(undefined);
  const isFrameMountedRef = useRef(false);
  const courseIdRef = useRef(courseId);
  // Read by the src builder and the `ready` reply, neither of which should re-run on a toggle.
  const themeModeRef = useRef(themeMode);

  courseIdRef.current = courseId;
  themeModeRef.current = themeMode;

  /** Tokens live 5 minutes and are spent by the first exchange — mint a fresh one every time. */
  const mintToken = useCallback(async (targetCourseId?: string): Promise<ReaderTokenResponse> => {
    // httpClient resolves (rather than throws) for statuses up to 500, so check it explicitly.
    const { status, data } = await httpClient.get<ReaderTokenResponse>('/reader/token', {
      params: targetCourseId ? { courseId: targetCourseId } : undefined,
    });

    if (status >= 400 || !data?.token) {
      throw new Error(`Reader token request failed with status ${status}`);
    }

    return data;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const openReader = async () => {
      // This host redirects below; don't spend a token mint on it.
      if (!isReaderAvailable()) {
        return;
      }

      const isFirstOpen = !isFrameMountedRef.current;

      // After the first open the frame is re-pointed in place: re-assigning src would reload the
      // whole reader (and re-run its offline caching) for what is a one-message change.
      if (!isFirstOpen && (!courseId || courseId === openedCourseIdRef.current)) {
        return;
      }

      try {
        setHasFailed(false);

        // The back end resolves the course itself when we don't know it yet (courses still loading).
        const data = await mintToken(courseId);

        if (isCancelled) {
          return;
        }

        openedCourseIdRef.current = data.courseId || courseId;

        if (isFirstOpen) {
          isFrameMountedRef.current = true;
          const targetCourseId = data.courseId || courseId;
          const query = new URLSearchParams();

          if (targetCourseId) {
            query.set('course', targetCourseId);
          }

          // In the query, not the fragment: browsers never transmit a fragment to a server, so a
          // mode passed there could only be applied once the reader's JS had run — a full-white
          // flash before the dark theme paints. In the query the reader renders dark in the first
          // byte. Sent on every open, like the freshly minted token: a re-mounted frame gets
          // whatever is in `src` and nothing else.
          query.set('theme', themeModeRef.current);

          setFrameSrc(`${READER_ORIGIN}/?${query}#token=${encodeURIComponent(data.token)}`);
        } else {
          // Re-points entitlement and navigates to that course's book, without a reload.
          postToReader(frameRef.current, { type: 'ipta-reader:set-course', token: data.token });
        }
      } catch (error) {
        if (!isCancelled) {
          setHasFailed(true);
        }
      }
    };

    openReader();

    return () => {
      isCancelled = true;
    };
  }, [courseId, mintToken, retryCount]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframe = frameRef.current;

      if (!iframe || event.source !== iframe.contentWindow) {
        return;
      }

      const message = (parseReaderMessage(event) || event.data) as ReaderInboundMessage | null;

      if (!message || typeof message !== 'object' || typeof message.type !== 'string') {
        return;
      }

      switch (message.type) {
        case 'ipta-reader:ready':
          // The URL already carried the mode, but the reader emits `ready` from the same
          // synchronous block that registers its listener, so this reply cannot arrive too early.
          postToReader(frameRef.current, { type: 'ipta-reader:theme', mode: themeModeRef.current });
          break;

        case 'ipta-reader:need-token':
          // Required: the reader's session lapsed, or it is opening itself in its own window
          // (the only way a student can install the offline app — it cannot be installed from an iframe).
          mintToken(courseIdRef.current)
            .then((data) =>
              postToReader(frameRef.current, {
                type: 'ipta-reader:token',
                token: data.token,
                ...(message.requestId ? { requestId: message.requestId } : {}),
              }),
            )
            // Deliberately not a page-level failure: the frame keeps whatever it is showing and
            // falls back to its own "open the reader from your course" screen.
            .catch(() => errorToast("We couldn't refresh your textbook session. Please reload the page."));
          break;

        case 'ipta-reader:cap-hit':
          // A trial student spent their AI tutor questions.
          openUpgradeModal();
          break;

        case 'ipta-reader:session':
          // Reader emits this when the student selects another textbook:
          // { type: 'ipta-reader:session', authenticated: true,
          //   courseId: '<LMS course UUID>', textbookId: '<reader book id>' }
          // Only update active course when the student explicitly picked a textbook
          // (identified by the presence of `textbookId`):
          if (
            message.authenticated !== true ||
            typeof message.courseId !== 'string' ||
            message.courseId.length === 0 ||
            typeof message.textbookId !== 'string' ||
            message.textbookId.length === 0 ||
            message.courseId === courseIdRef.current
          ) {
            return;
          }

          openedCourseIdRef.current = message.courseId;
          dispatch(
            actions.courses.setActiveCourse({
              courseId: message.courseId,
              accentColor: message.accentColor,
            }),
          )
            .unwrap()
            .catch(() => {
              openedCourseIdRef.current = courseIdRef.current;
              errorToast("We couldn't switch your active course. Please try again.");
            });
          break;

        case 'ipta-reader:chrome':
          // The parser validates origin and type only — guard the payload shape here.
          if (typeof message.collapse === 'boolean') {
            // Never hide the header while the hamburger overlay is open (it scroll-locks the
            // body): the overlay escapes HeaderSlot's clip, so collapsing would remove the
            // close button and strand the menu. Iframe momentum scrolling can still emit
            // collapse signals in the instant after the menu opens.
            if (message.collapse && document.body.style.overflow === 'hidden') {
              break;
            }
            setIsChromeCollapsed(message.collapse);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch, errorToast, mintToken, openUpgradeModal]);

  /**
   * Keeps the reader in step with a toggle flipped while it is open — a full-page frame a student
   * may keep open for a whole study session, so waiting for a navigation is not good enough.
   *
   * Deliberately a message and not a fresh `src`: re-assigning `src` reloads the frame and throws
   * away the scroll position, the open table of contents and any in-progress AI tutor conversation.
   */
  useEffect(() => {
    postToReader(frameRef.current, { type: 'ipta-reader:theme', mode: themeMode });
  }, [themeMode]);

  // Lets a sign-out anywhere in the LMS clear the reader's own 30-day session.
  useEffect(() => {
    if (!frameSrc || !frameRef.current) {
      return;
    }

    return registerReaderFrame(frameRef.current);
  }, [frameSrc]);

  // A brand the reader can't serve or a user without certification courses has no
  // Textbook access — this covers a bookmarked or hand-typed /reader.
  if (!isReaderAvailable() || (courses !== null && !canUserAccessTextbook(courses, activeCourse))) {
    return <Navigate to={routes.home} replace />;
  }

  return (
    <Layout>
      <HeaderSlot $collapsed={isChromeCollapsed}>
        <StyledSidebar />
      </HeaderSlot>
      <Wrapper>
        {hasFailed && (
          <Placeholder>
            <StyledText font="body_large_semibold_16">We couldn&apos;t open your textbook.</StyledText>
            <RetryButton variant="primary" onClick={() => setRetryCount((count) => count + 1)}>
              Try again
            </RetryButton>
          </Placeholder>
        )}
        {!hasFailed && !frameSrc && <Loader />}
        {!hasFailed && frameSrc && (
          // No `sandbox`: the reader needs same-origin storage (IndexedDB + service worker) for
          // offline reading, and a sandbox without allow-same-origin silently disables it.
          <Frame ref={frameRef} src={frameSrc} title="Textbook" allow="clipboard-write; fullscreen; microphone" />
        )}
      </Wrapper>
      <UpgradeSubscriptionModal
        isOpen={isUpgradeModalOpen}
        onClose={closeUpgradeModal}
        manualType={ManualTypeEnum.StudyGuideUpgrade}
      />
    </Layout>
  );
};

export default Reader;

const Layout = styled.div`
  width: 100%;
  height: calc(var(--vh, 1vh) * 100);

  margin: 0;
  padding: 0;

  display: grid;
  grid-template-columns: auto 1fr;
  overflow: hidden;

  background-color: ${({ theme: { colors } }) => colors.neutrals[10]};

  ${respondToWidth.menu`
    grid-template-columns: 1fr;
    /* The header row is sized by HeaderSlot so the reader can collapse it to 0. */
    grid-template-rows: auto 1fr;
  `}
`;

// No right margin, unlike the other pages: the reader is a full app of its own and should meet
// the sidebar edge to edge rather than sit in the LMS's content gutter.
const StyledSidebar = styled(Sidebar)`
  margin-right: 0;
`;

/**
 * Owns the header's footprint so the reader's `ipta-reader:chrome` signal can collapse it: on
 * phones the slot animates 65px -> 0 (a fixed-px height animation — grid-track transitions snap
 * on iOS < 16) and the `auto` row follows, letting the iframe take the whole viewport. Desktop
 * never collapses (the reader only emits on phone viewports), so the slot is a pass-through.
 */
const HeaderSlot = styled.div<{ $collapsed: boolean }>`
  height: 100%;
  min-height: 0;

  ${({ $collapsed }) => respondToWidth.menu`
    /* LAYOUT snaps instantly — the reader counter-scrolls by the same 65px in the same frame,
       which keeps the reading line pixel-stable. Only the VISUAL slides: the bar keeps its own
       65px height and translates out over the (already expanded) frame, browser-bar style. */
    height: ${$collapsed ? '0px' : '65px'};
    overflow: visible;

    & > * {
      height: 65px !important;
      /* 'none' at rest on purpose, and NO position on this slot: the hamburger menu's overlay
         is viewport-anchored absolute positioning, so any positioned ancestor (or lingering
         transform) would pin it inside this 65px bar. While collapsing, the transform itself
         paints the sliding bar above the static iframe — no z-index needed. */
      transform: ${$collapsed ? 'translateY(-100%)' : 'none'};
      transition: transform 0.25s ease;
    }
  `}
`;

/**
 * A fixed-height container, deliberately: the reader is a full-height app with its own scrolling,
 * so a frame that grows to its content produces nested scrollbars and breaks its sticky chrome.
 */
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const Frame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const RetryButton = styled(Button)`
  width: fit-content;
  padding: 0 24px;
`;
